import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Link2, Copy, Check, RefreshCw } from 'lucide-react';

export default function SettingsPage() {
  const [inviteUrl, setInviteUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [invites, setInvites] = useState([]);

  const loadInvites = () => api.get('/auth/invites').then(r => setInvites(r.data.invites)).catch(() => {});
  useEffect(() => { loadInvites(); }, []);

  const generateInvite = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/auth/invite');
      setInviteUrl(res.data.invite_url);
      loadInvites();
    } catch (err) {
      alert('Failed to generate invite');
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = inviteUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: { background: 'rgba(34,197,94,0.15)', color: '#22c55e' },
      used:    { background: 'rgba(156,163,175,0.15)', color: '#9ca3af' },
      expired: { background: 'rgba(239,68,68,0.15)', color: '#ef4444' },
    };
    const s = styles[status] || styles.pending;
    return (
      <span style={{
        ...s, padding: '2px 10px', borderRadius: '12px', fontSize: '12px',
        fontWeight: '600', textTransform: 'capitalize',
      }}>{status}</span>
    );
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Settings</h1>
      </div>

      <div className="card" style={{ maxWidth: '720px' }}>
        <div className="card-body">
          <h2 style={{ fontSize: '18px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Link2 size={18} /> Invite Users
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Generate a one-time invite link to let someone create an account. Links expire after 7 days.
          </p>

          <button className="btn btn-primary" onClick={generateInvite} disabled={generating} style={{ marginBottom: '16px' }}>
            <RefreshCw size={14} className={generating ? 'spin' : ''} />
            {generating ? 'Generating...' : 'Generate Invite Link'}
          </button>

          {inviteUrl && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              <input
                readOnly value={inviteUrl}
                style={{ flex: 1, fontSize: '13px', fontFamily: 'monospace', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', color: 'var(--text)' }}
                onClick={e => e.target.select()}
              />
              <button className="btn btn-secondary" onClick={copyLink} style={{ whiteSpace: 'nowrap' }}>
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Link</>}
              </button>
            </div>
          )}

          {invites.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-muted)', fontWeight: '500' }}>Token</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-muted)', fontWeight: '500' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-muted)', fontWeight: '500' }}>Created</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-muted)', fontWeight: '500' }}>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map(inv => (
                    <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{inv.token.slice(0, 8)}...</td>
                      <td style={{ padding: '8px 12px' }}>{statusBadge(inv.status)}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>{new Date(inv.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>{new Date(inv.expires_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
